import { yupResolver } from '@hookform/resolvers/yup';
import cn from 'classnames';
import { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import zxcvbn from 'zxcvbn';
import { Button } from './components/common/Button';
import LanguageButton from './components/common/LanguageButton';
import StatusIndicator from './components/common/StatusIndicator';
import {
    ResetPasswordWithTokenFormInput,
    ResetPasswordWithTokenFormValues,
    resetPasswordWithTokenSchema
} from './components/form/schema/resetPasswordWithTokenSchema';
import RHFInput from './components/inputs/RHFInput';
import { LoginFooter, LoginLogo } from './Login';
import { logToServer } from './services/LogService';
import { resetUserPasswordWithToken } from './services/UserService';
import useLanguage from './utils/hooks/useLanguage';

interface ExtendedResetPasswordWithTokenFormValues extends ResetPasswordWithTokenFormInput {
    server: unknown;
}

const defaultValues: Partial<ResetPasswordWithTokenFormInput> = {
    token: undefined,
    username: undefined,
    new_password: undefined,
    confirm_new_password: undefined
};

function ResetPassword() {
    const lang = useLanguage();

    const [searchParams] = useSearchParams();

    const navigate = useNavigate();
    const [resetPasswordResponse, setResetPasswordResponse] = useState<string>();
    const form = useForm<ExtendedResetPasswordWithTokenFormValues>({
        defaultValues,
        mode: 'onChange',
        resolver: yupResolver(resetPasswordWithTokenSchema)
    });
    const {
        watch,
        trigger,
        register,
        setError,
        setValue,
        handleSubmit,
        formState: { isSubmitting, isValid, errors }
    } = form;

    const onSubmit = async (data: ResetPasswordWithTokenFormInput) => {
        const dataValidated = data as ResetPasswordWithTokenFormValues;
        const { token, username, new_password, confirm_new_password } = dataValidated;
        if (new_password === confirm_new_password) {
            const resp = await resetUserPasswordWithToken(token, username, new_password);
            if (resp.succeeded) {
                logToServer('info', `User [${username}] has changed password.`);
                setResetPasswordResponse(resp.message[0]);
                // handlers.close();
            } else {
                const body = resp.message[0];
                setError('server', { type: 'custom', message: body });
                logToServer('info', `User [${username}] has trouble changing password.`);
            }
        } else {
            setError('confirm_new_password', {
                type: 'custom',
                message: lang.resetPassword.passwordMismatchError
            });
        }
    };

    const [new_password, confirm_new_password] = watch(['new_password', 'confirm_new_password']);

    useEffect(() => {
        if (confirm_new_password) trigger('confirm_new_password');
    }, [new_password, confirm_new_password]);

    const passwordStrength = useMemo(() => {
        if (!new_password) return 0;
        return zxcvbn(new_password).score;
    }, [new_password]);

    const strengthInfo = useMemo(() => {
        if (passwordStrength === 0)
            return {
                label: '',
                color: 'bg-transparent',
                text: 'text-brand-neutral-400'
            };
        if (passwordStrength <= 1)
            return {
                label: 'Weak',
                color: 'bg-red-500',
                text: 'text-red-500'
            };
        if (passwordStrength === 2)
            return {
                label: 'Medium',
                color: 'bg-yellow-500',
                text: 'text-yellow-600'
            };
        if (passwordStrength === 3)
            return {
                label: 'Good',
                color: 'bg-brand-green-light',
                text: 'text-brand-green-light'
            };
        return {
            label: 'Strong',
            color: 'bg-brand-green',
            text: 'text-brand-green'
        };
    }, [passwordStrength]);

    useEffect(() => {
        const token = searchParams.get('token');
        const username = searchParams.get('username');

        if (token && username) {
            setValue('token', token);
            setValue('username', username);
        } else {
            setError('server', { type: 'custom', message: lang.resetPassword.missingTokenError });
        }
    }, []);

    return (
        <div className="h-full bg-brand-background flex flex-col justify-center py-12 px-6">
            <LoginLogo />
            <div className="absolute top-6 right-6">
                <LanguageButton className="w-4 h-4" />
            </div>
            {resetPasswordResponse ? (
                <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
                    <div className="p-6 sm:p-8 rounded-md sm:rounded-lg sm:px-10 bg-brand-background-dark border-black shadow-[0_20px_76px_33px_rgba(0,0,0,0.66)]">
                        <div className="text-lg text-brand-primary-light whitespace-nowrap text-center">
                            <p>{lang.resetPassword.successMessage}</p>
                        </div>
                        <div
                            className="mt-6 relative flex justify-center text-sm font-medium text-brand-neutral-300 hover:text-brand-text cursor-pointer"
                            onClick={() => navigate('/login')}>
                            {lang.resetPassword.backToLogin}
                        </div>
                    </div>
                </div>
            ) : (
                <FormProvider {...form}>
                    <form className="relative" onSubmit={handleSubmit(onSubmit)}>
                        <div className="relative my-6 mx-auto w-full max-w-md">
                            <div className="p-4 sm:p-6 rounded-md sm:rounded-lg sm:px-8 bg-brand-background brand-shadow z-0">
                                <div className="absolute top-4 right-4">
                                    <StatusIndicator showLabel={false} size="md" />
                                </div>
                                <div className="flex flex-col gap-5">
                                    <div className="flex gap-1 items-center justify-center text-center text-sm mb-4">
                                        <p className="text-brand-neutral-200">{lang.resetPassword.title}</p>
                                    </div>
                                    <RHFInput
                                        {...register('username')}
                                        label={lang.resetPassword.username}
                                        placeholder={lang.resetPassword.username}
                                        type="text"
                                        required
                                        disabled
                                    />
                                    <input type="password" className="opacity-0 none absolute" />
                                    <div className={cn('', { 'space-y-3 sm:space-y-2': new_password })}>
                                        <RHFInput
                                            {...register('new_password')}
                                            label={lang.resetPassword.newPassword}
                                            placeholder={lang.resetPassword.newPassword}
                                            type="password"
                                            autoComplete="off"
                                            required
                                        />
                                        <div
                                            className={`space-y-3 transition-all duration-300 ease-in-out overflow-hidden ${
                                                new_password ? 'max-h-64 opacity-100' : 'max-h-0 opacity-0'
                                            }`}
                                            id="password-strength">
                                            <div className="flex justify-between items-center mb-1">
                                                <span className="text-xs text-brand-neutral-300">
                                                    Password Strength
                                                </span>
                                                <span
                                                    className={`text-xs font-bold transition-colors duration-300 ${strengthInfo.text}`}>
                                                    {strengthInfo.label}
                                                </span>
                                            </div>

                                            {/* Progress Bar */}
                                            <div className="h-2 w-full bg-brand-neutral-600 rounded-full overflow-hidden">
                                                <div
                                                    className={`h-full transition-all duration-500 ease-out ${strengthInfo.color}`}
                                                    style={{
                                                        width: `${passwordStrength * 25}%`
                                                    }}
                                                    role="progressbar"
                                                    aria-valuenow={passwordStrength * 25}
                                                    aria-valuemin={0}
                                                    aria-valuemax={100}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <RHFInput
                                        {...register('confirm_new_password')}
                                        label={lang.resetPassword.confirmNewPassword}
                                        placeholder={lang.resetPassword.confirmNewPassword}
                                        type="password"
                                        required
                                    />
                                    {errors['server'] && (
                                        <span className="text-brand-red italic">{errors['server'].message}</span>
                                    )}
                                    <Button
                                        className="rounded-md p-2 px-4 bg-brand-primary hover:bg-brand-primary-light"
                                        isLoading={isSubmitting}
                                        disabled={isSubmitting || !(passwordStrength >= 3) || !isValid}>
                                        {lang.resetPassword.changePasswordButton}
                                    </Button>
                                </div>
                                <div
                                    className="mt-6 relative flex justify-center text-sm font-medium text-brand-neutral-300 hover:text-brand-text cursor-pointer"
                                    onClick={() => navigate('/login')}>
                                    {lang.resetPassword.backToLogin}
                                </div>
                            </div>
                        </div>
                        <LoginFooter />
                    </form>
                </FormProvider>
            )}
        </div>
    );
}

export default ResetPassword;
